// screen: Registered viewers — list with counts, search and filters (change viewer-crm)
// Registered viewers — list (change viewer-crm, design §4.1). Counts, search and filters kept in the URL,
// masked identity without viewers:pii (initials, d***@gmail.com; search by id only), cursor paging, profile link for
// Admins and Owners.
import * as React from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { Tv, UsersRound } from 'lucide-react';
import type { ApiError } from '@viewstream/api-client';
import { Badge, Button, EmptyState, ErrorState, Input, KpiTile, Select, Skeleton } from '@viewstream/ui';
import { fmtDate, fmtNumber } from '@viewstream/i18n';
import { appTitle } from '~/app/brand';
import { useTenant } from '~/app/tenant';
import { useLocale } from '~/app/locale';
import { can } from '~/app/permissions';
import { useSegments, useViewerCounts, useViewers, type ViewerFilters } from '~/app/viewers';
import { usePlatformLabel, useStatusLabel, ViewersHeader, VIEWERS_PII, VIEWERS_READ } from '~/app/ViewersNav';

const STR = ['q', 'joined_from', 'joined_to', 'platform', 'method', 'status', 'segment_id', 'sort', 'cursor'] as const;

export const Route = createFileRoute('/_layout/t/$slug/viewers')({
  component: Screen,
  validateSearch: (s: Record<string, unknown>): ViewerFilters => {
    const out: ViewerFilters = {};
    for (const k of STR) if (typeof s[k] === 'string' && (s[k] as string).length <= 200) (out as Record<string, string>)[k] = s[k] as string;
    for (const k of ['active_days', 'inactive_days'] as const) { const n = Number(s[k]); if (Number.isInteger(n) && n > 0 && n <= 3650) out[k] = n; }
    if (s['marketing'] === 'true' || s['marketing'] === 'false') out.marketing = s['marketing'];
    if (s['order'] === 'asc' || s['order'] === 'desc') out.order = s['order'];
    return out;
  },
});

function Screen() {
  const { t } = useLingui();
  const { tenant, scopes } = useTenant();
  const { locale, tz } = useLocale();
  const search = Route.useSearch();
  const navigate = useNavigate();
  React.useEffect(() => { document.title = appTitle(t`Registered viewers`); }, [t]);
  const pii = can(scopes, VIEWERS_PII);
  const counts = useViewerCounts(tenant.slug);
  const list = useViewers(tenant.slug, search);
  const segs = useSegments(tenant.slug);
  const platform = usePlatformLabel();
  const status = useStatusLabel();
  const [q, setQ] = React.useState(search.q ?? '');
  const go = (next: Partial<ViewerFilters>, keepCursor = false) => {
    const s: ViewerFilters = { ...search, ...next };
    if (!keepCursor) delete s.cursor;
    for (const k of Object.keys(s) as (keyof ViewerFilters)[]) if (s[k] === '' || s[k] === undefined) delete s[k];
    void navigate({ to: '/t/$slug/viewers' as never, params: { slug: tenant.slug } as never, search: s as never });
  };
  if (!can(scopes, VIEWERS_READ)) return <p className="p-6 text-sm">{t`You do not have access to registered viewers.`}</p>;
  const c = counts.data;
  const num = (n: number | undefined) => (n === undefined ? '—' : fmtNumber(n, locale, { maximumFractionDigits: 0 }));
  const all = { value: '', label: t`All` };

  return (
    <div>
      <ViewersHeader tab="list" />
      {c && !c.accounts_available ? (
        <p className="mb-4 rounded-md border border-warn/30 bg-warn/10 p-3 text-sm" data-testid="viewers-not-available">
          {t`Viewer accounts are not switched on yet. Once a site lets viewers register, they appear here.`}
        </p>
      ) : null}
      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="viewers-kpis">
        <KpiTile label={t`Registered`} value={num(c?.registered)} />
        <KpiTile label={t`New in 30 days`} value={num(c?.new_30d)} />
        <KpiTile label={t`Active in 30 days`} value={num(c?.active_30d)} />
        <KpiTile label={t`With marketing consent`} value={num(c?.marketing)} />
      </div>
      <form className="mb-3 flex flex-wrap items-end gap-2" onSubmit={(e) => { e.preventDefault(); go({ q }); }}>
        <Input value={q} onChange={(e) => setQ(e.target.value)} className="w-72" aria-label={t`Search`}
          placeholder={pii ? t`Search: name, e-mail or id` : t`Search by viewer id`} data-testid="viewers-search" />
        <Button type="submit" variant="secondary">{t`Search`}</Button>
        <Select ariaLabel={t`Last active`} value={search.active_days ? `a${search.active_days}` : search.inactive_days ? `i${search.inactive_days}` : ''}
          onValueChange={(v) => go({ active_days: v.startsWith('a') ? Number(v.slice(1)) : undefined, inactive_days: v.startsWith('i') ? Number(v.slice(1)) : undefined })}
          options={[{ value: '', label: t`Any activity` }, { value: 'a7', label: t`Active in 7 days` }, { value: 'a30', label: t`Active in 30 days` }, { value: 'a90', label: t`Active in 90 days` }, { value: 'i90', label: t`Not seen for 90 days` }]} />
        <Select ariaLabel={t`Platform`} value={search.platform ?? ''} onValueChange={(v) => go({ platform: v })}
          options={[{ value: '', label: t`All platforms` }, ...['tizen', 'webos', 'androidtv', 'firetv', 'vidaa', 'web', 'ios', 'android'].map((p) => ({ value: p, label: platform(p) }))]} />
        <Select ariaLabel={t`Sign-in method`} value={search.method ?? ''} onValueChange={(v) => go({ method: v })}
          options={[{ value: '', label: t`All sign-in methods` }, { value: 'password', label: t`Password` }, { value: 'google', label: 'Google' }, { value: 'apple', label: 'Apple' }]} />
        <Select ariaLabel={t`Marketing consent`} value={search.marketing ?? ''} onValueChange={(v) => go({ marketing: (v || undefined) as ViewerFilters['marketing'] })}
          options={[{ value: '', label: t`Marketing: all` }, { value: 'true', label: t`Agreed to marketing` }, { value: 'false', label: t`No marketing` }]} />
        <Select ariaLabel={t`Status`} value={search.status ?? ''} onValueChange={(v) => go({ status: v })}
          options={[{ ...all, label: t`Active and blocked` }, { value: 'active', label: status('active') }, { value: 'blocked', label: status('blocked') }, { value: 'deleted', label: status('deleted') }]} />
        {segs.data && segs.data.length > 0 ? (
          <Select ariaLabel={t`Segment`} value={search.segment_id ?? ''} onValueChange={(v) => go({ segment_id: v })}
            options={[{ value: '', label: t`All segments` }, ...segs.data.map((s) => ({ value: s.id, label: s.name }))]} />
        ) : null}
        {Object.keys(search).length > 0 ? <Button type="button" variant="ghost" onClick={() => { setQ(''); void navigate({ to: '/t/$slug/viewers' as never, params: { slug: tenant.slug } as never, search: {} as never }); }}>{t`Clear filters`}</Button> : null}
      </form>
      {list.isPending ? <Skeleton className="h-40 w-full" /> : list.isError ? (
        <ErrorState error={list.error as ApiError} onRetry={() => list.refetch()} labels={{ title: t`Could not load the viewers`, retry: t`Retry`, requestId: t`Request id`, copy: t`Copy` }} />
      ) : !list.data || list.data.items.length === 0 ? (
        <EmptyState icon={<UsersRound className="size-6" aria-hidden />} title={t`No registered viewers match`} />
      ) : (
        <>
          <p className="mb-2 text-sm text-fg-muted">{t`${num(list.data.total)} viewers`}{!pii ? <> · {t`Names and e-mails are masked: personal data is shown to Admins and Owners.`}</> : null}</p>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm" data-testid="viewers-table">
              <thead className="bg-surface-2 text-start text-fg-muted">
                <tr>
                  <Th label={t`Name`} />
                  <Th label={t`E-mail`} sort="email" search={search} go={go} />
                  <Th label={t`Joined`} sort="created_at" search={search} go={go} />
                  <Th label={t`Last seen`} sort="last_seen_at" search={search} go={go} />
                  <Th label={t`Devices`} />
                  <Th label={t`Marketing`} />
                  <Th label={t`Status`} sort="status" search={search} go={go} />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {list.data.items.map((v) => (
                  <tr key={v.id} data-viewer-id={v.id}>
                    <td className="px-3 py-2">
                      {pii ? <Link to={'/t/$slug/viewers/$viewerId' as never} params={{ slug: tenant.slug, viewerId: v.id } as never} className="text-primary hover:underline">{v.name || t`(no name)`}</Link> : (v.name || '—')}
                    </td>
                    <td className="px-3 py-2"><bdi dir="ltr">{v.email}</bdi>{!v.verified ? <Badge className="ms-2" tone="warn">{t`Not verified`}</Badge> : null}</td>
                    <td className="px-3 py-2">{fmtDate(v.created_at, { locale, tz })}</td>
                    <td className="px-3 py-2">{v.last_seen_at ? fmtDate(v.last_seen_at, { locale, tz }) : '—'}</td>
                    <td className="px-3 py-2"><span className="inline-flex items-center gap-1">{v.devices}{v.tv ? <Tv className="size-4" aria-label={t`Signed in on a TV`} /> : null}</span></td>
                    <td className="px-3 py-2">{v.marketing ? '✓' : '—'}</td>
                    <td className="px-3 py-2"><Badge tone={v.status === 'active' ? 'ok' : v.status === 'blocked' ? 'danger' : 'neutral'}>{status(v.status)}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 flex gap-2">
            {search.cursor ? <Button variant="ghost" onClick={() => go({})}>{t`First page`}</Button> : null}
            {list.data.next_cursor ? <Button variant="secondary" onClick={() => go({ cursor: list.data!.next_cursor! }, true)} data-testid="viewers-next">{t`Next page`}</Button> : null}
          </div>
        </>
      )}
    </div>
  );
}

function Th({ label, sort, search, go }: { label: string; sort?: string; search?: ViewerFilters; go?: (n: Partial<ViewerFilters>) => void }) {
  if (!sort || !search || !go) return <th className="px-3 py-2 text-start font-medium">{label}</th>;
  const active = (search.sort ?? 'created_at') === sort;
  const asc = active && search.order === 'asc';
  return (
    <th className="px-3 py-2 text-start font-medium" aria-sort={active ? (asc ? 'ascending' : 'descending') : 'none'}>
      <button type="button" className="hover:text-fg" onClick={() => go({ sort, order: active && !asc ? 'asc' : 'desc' })}>{label}{active ? (asc ? ' ↑' : ' ↓') : ''}</button>
    </th>
  );
}
